// src/components/admin/AdminContent.js
import React, { useState, useEffect, useRef } from 'react';
import { storage } from '../../services/firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  getMotivationalMessages, saveMotivationalMessage, deleteMotivationalMessage,
  getNotificationMessages, deleteNotificationMessage,
  getFAQs, saveFAQ, deleteFAQ
} from '../../services/firebase/firestoreService';
import { db } from '../../services/firebase/config';
import {
  collection, getDocs, doc, setDoc, deleteDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const validateFile = (file) => {
  if (file.size > MAX_FILE_SIZE) return 'File must be under 2MB';
  return null;
};

export default function AdminContent() {
  const [section, setSection] = useState('motivational');
  const [statusMsg, setStatusMsg] = useState(null);

  const showMsg = (text, type = 'success') => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg(null), 4000);
  };

  const SECTIONS = [
    { id: 'motivational', label: 'Motivational Messages' },
    { id: 'notifications', label: 'Notification Messages' },
    { id: 'faqs', label: 'FAQs' },
    { id: 'audio', label: 'Audio' },
  ];

  return (
    <div>
      <div className="admin-section-title">Content</div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {SECTIONS.map(s => (
          <button key={s.id}
            className={`admin-btn ${section === s.id ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setSection(s.id)}>
            {s.label}
          </button>
        ))}
      </div>
      {statusMsg && (
        <div className={`admin-status-msg ${statusMsg.type}`} style={{ marginBottom: '16px' }}>
          {statusMsg.text}
        </div>
      )}
      {section === 'motivational' && <MotivationalMessages showMsg={showMsg} />}
      {section === 'notifications' && <NotificationMessages showMsg={showMsg} />}
      {section === 'faqs' && <FAQsSection showMsg={showMsg} />}
      {section === 'audio' && <AudioSection showMsg={showMsg} />}
    </div>
  );
}

// ===== MOTIVATIONAL MESSAGES =====
function MotivationalMessages({ showMsg }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ mr: '', en: '', active: true });
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getMotivationalMessages();
      setMessages(data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.mr || !form.en) { showMsg('Both Marathi and English text required', 'error'); return; }
    if (!window.confirm(editingId ? 'Save changes?' : 'Add this message?')) return;
    setLoading(true);
    try {
      await saveMotivationalMessage(editingId, { mr: form.mr, en: form.en, active: form.active });
      showMsg(editingId ? 'Message updated' : 'Message added');
      setForm({ mr: '', en: '', active: true });
      setEditingId(null);
      setPreview(null);
      load();
    } catch (e) {
      showMsg('Error saving', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message permanently?')) return;
    try {
      await deleteMotivationalMessage(id);
      showMsg('Message deleted');
      load();
    } catch (e) {
      showMsg('Error deleting', 'error');
    }
  };

  const handleToggleActive = async (msg) => {
    if (!window.confirm(`Mark as ${msg.active ? 'inactive' : 'active'}?`)) return;
    try {
      await saveMotivationalMessage(msg.id, { ...msg, active: !msg.active });
      load();
    } catch (e) {
      showMsg('Error updating', 'error');
    }
  };

  return (
    <div>
      <div className="admin-card">
        <h3>{editingId ? 'Edit Message' : 'Add New Message'}</h3>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Marathi Text</label>
            <textarea value={form.mr} onChange={e => setForm({ ...form, mr: e.target.value })}
              style={{ minHeight: '80px', resize: 'vertical', whiteSpace: 'pre-wrap' }}
              placeholder="मराठी मजकूर..." />
          </div>
          <div className="admin-form-group">
            <label>English Text</label>
            <textarea value={form.en} onChange={e => setForm({ ...form, en: e.target.value })}
              style={{ minHeight: '80px', resize: 'vertical', whiteSpace: 'pre-wrap' }}
              placeholder="English text..." />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <input type="checkbox" checked={form.active}
              onChange={e => setForm({ ...form, active: e.target.checked })} />
            Active
          </label>
          <button className="admin-btn admin-btn-secondary"
            onClick={() => setPreview(form.mr || form.en ? form : null)}>
            Preview
          </button>
        </div>
        {preview && (
          <div style={{ background: '#1a1a2e', borderRadius: '12px', padding: '20px', marginBottom: '12px', textAlign: 'center' }}>
            <p style={{ color: '#fff', fontSize: '16px', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>{preview.mr}</p>
            <p style={{ color: '#aaa', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{preview.en}</p>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : editingId ? 'Save Changes' : 'Add Message'}
          </button>
          {editingId && (
            <button className="admin-btn admin-btn-secondary"
              onClick={() => { setEditingId(null); setForm({ mr: '', en: '', active: true }); setPreview(null); }}>
              Cancel
            </button>
          )}
        </div>
      </div>
      <div className="admin-card">
        <h3>All Messages ({messages.length})</h3>
        {loading ? <p>लोड होत आहे...</p> : messages.map(msg => (
          <div key={msg.id}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
              marginBottom: '8px', background: msg.active ? '#f8f9fa' : '#fef2f2',
              borderRadius: '8px', border: '1px solid #e9ecef' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 500, whiteSpace: 'pre-wrap' }}>{msg.mr}</div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '2px', whiteSpace: 'pre-wrap' }}>{msg.en}</div>
            </div>
            <span style={{ fontSize: '12px', color: msg.active ? '#28a745' : '#dc3545', fontWeight: 600, minWidth: '55px' }}>
              {msg.active ? 'Active' : 'Inactive'}
            </span>
            <button className="admin-btn admin-btn-secondary" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => { setEditingId(msg.id); setForm({ mr: msg.mr, en: msg.en, active: msg.active }); }}>
              Edit
            </button>
            <button className="admin-btn" style={{ padding: '4px 10px', fontSize: '13px',
              background: msg.active ? '#fff3cd' : '#d4edda', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              onClick={() => handleToggleActive(msg)}>
              {msg.active ? 'Deactivate' : 'Activate'}
            </button>
            <button className="admin-btn admin-btn-danger" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => handleDelete(msg.id)}>
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== NOTIFICATION MESSAGES =====
function NotificationMessages({ showMsg }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', active: true });
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getNotificationMessages();
      setMessages(data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.message) { showMsg('Title and message required', 'error'); return; }
    if (!window.confirm(editingId ? 'Save changes?' : 'Add this message?')) return;
    setLoading(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, 'notificationMessages', editingId), {
          title: form.title, message: form.message, active: form.active,
          updatedAt: serverTimestamp()
        });
      } else {
        await setDoc(doc(collection(db, 'notificationMessages')), {
          title: form.title, message: form.message, active: form.active,
          createdAt: serverTimestamp()
        });
      }
      showMsg(editingId ? 'Message updated' : 'Message added');
      setForm({ title: '', message: '', active: true });
      setEditingId(null);
      setPreview(null);
      load();
    } catch (e) {
      showMsg('Error saving', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message permanently?')) return;
    try {
      await deleteNotificationMessage(id);
      showMsg('Message deleted');
      load();
    } catch (e) {
      showMsg('Error deleting', 'error');
    }
  };

  const handleToggleActive = async (msg) => {
    if (!window.confirm(`Mark as ${msg.active ? 'inactive' : 'active'}?`)) return;
    try {
      await updateDoc(doc(db, 'notificationMessages', msg.id), { active: !msg.active });
      load();
    } catch (e) {
      showMsg('Error updating', 'error');
    }
  };

  return (
    <div>
      <div className="admin-card">
        <h3>{editingId ? 'Edit Message' : 'Add New Message'}</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          These messages are picked randomly for the 5:30am daily push notification.
        </p>
        <div className="admin-form-group" style={{ marginBottom: '12px' }}>
          <label>Title (appears bold)</label>
          <input type="text" value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="आजचा दिवस तुमचा आहे 💪" />
        </div>
        <div className="admin-form-group" style={{ marginBottom: '12px' }}>
          <label>Message</label>
          <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
            style={{ minHeight: '80px', resize: 'vertical' }}
            placeholder="सराव सुरू करा आणि आजचा दिवस जिंका!" />
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <input type="checkbox" checked={form.active}
              onChange={e => setForm({ ...form, active: e.target.checked })} />
            Active
          </label>
          <button className="admin-btn admin-btn-secondary"
            onClick={() => setPreview(form.title && form.message ? form : null)}>
            Preview
          </button>
        </div>
        {preview && (
          <div style={{ background: '#1a1a2e', borderRadius: '12px', padding: '20px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <img src="/icons/icon-72x72.png" alt="icon"
                style={{ width: '40px', height: '40px', borderRadius: '8px', flexShrink: 0 }} />
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>{preview.title}</div>
                <div style={{ color: '#ccc', fontSize: '14px', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                  {preview.message}
                </div>
              </div>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : editingId ? 'Save Changes' : 'Add Message'}
          </button>
          {editingId && (
            <button className="admin-btn admin-btn-secondary"
              onClick={() => { setEditingId(null); setForm({ title: '', message: '', active: true }); setPreview(null); }}>
              Cancel
            </button>
          )}
        </div>
      </div>
      <div className="admin-card">
        <h3>All Messages ({messages.length})</h3>
        {loading ? <p>लोड होत आहे...</p> : messages.map(msg => (
          <div key={msg.id}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
              marginBottom: '8px', background: msg.active ? '#f8f9fa' : '#fef2f2',
              borderRadius: '8px', border: '1px solid #e9ecef' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 700 }}>{msg.title}</div>
              <div style={{ fontSize: '13px', color: '#555', marginTop: '2px', whiteSpace: 'pre-wrap' }}>{msg.message}</div>
            </div>
            <span style={{ fontSize: '12px', color: msg.active ? '#28a745' : '#dc3545', fontWeight: 600, minWidth: '55px' }}>
              {msg.active ? 'Active' : 'Inactive'}
            </span>
            <button className="admin-btn admin-btn-secondary" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => { setEditingId(msg.id); setForm({ title: msg.title || '', message: msg.message || '', active: msg.active ?? true }); }}>
              Edit
            </button>
            <button className="admin-btn" style={{ padding: '4px 10px', fontSize: '13px',
              background: msg.active ? '#fff3cd' : '#d4edda', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              onClick={() => handleToggleActive(msg)}>
              {msg.active ? 'Deactivate' : 'Activate'}
            </button>
            <button className="admin-btn admin-btn-danger" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => handleDelete(msg.id)}>
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== FAQs =====
function FAQsSection({ showMsg }) {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ questionMr: '', questionEn: '', answerMr: '', answerEn: '', active: true });
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getFAQs();
      setFaqs(data.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.questionMr || !form.questionEn || !form.answerMr || !form.answerEn) {
      showMsg('All fields required', 'error'); return;
    }
    if (!window.confirm(editingId ? 'Save changes?' : 'Add this FAQ?')) return;
    setLoading(true);
    try {
      await saveFAQ(editingId, { ...form, order: editingId ? undefined : faqs.length });
      showMsg(editingId ? 'FAQ updated' : 'FAQ added');
      setForm({ questionMr: '', questionEn: '', answerMr: '', answerEn: '', active: true });
      setEditingId(null);
      setPreview(null);
      load();
    } catch (e) {
      showMsg('Error saving', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ permanently?')) return;
    try {
      await deleteFAQ(id);
      showMsg('FAQ deleted');
      load();
    } catch (e) {
      showMsg('Error deleting', 'error');
    }
  };

  const handleToggleActive = async (faq) => {
    if (!window.confirm(`Mark as ${faq.active ? 'inactive' : 'active'}?`)) return;
    try {
      await saveFAQ(faq.id, { ...faq, active: !faq.active });
      load();
    } catch (e) {
      showMsg('Error updating', 'error');
    }
  };

  const handleDragStart = (e, id) => { dragItem.current = id; };
  const handleDragOver = (e, id) => { e.preventDefault(); dragOver.current = id; };
  const handleDrop = async () => {
    if (!dragItem.current || !dragOver.current || dragItem.current === dragOver.current) return;
    const reordered = [...faqs];
    const from = reordered.findIndex(f => f.id === dragItem.current);
    const to = reordered.findIndex(f => f.id === dragOver.current);
    reordered.splice(to, 0, reordered.splice(from, 1)[0]);
    setFaqs(reordered);
    for (let i = 0; i < reordered.length; i++) {
      await saveFAQ(reordered[i].id, { ...reordered[i], order: i });
    }
    dragItem.current = null;
    dragOver.current = null;
  };

  return (
    <div>
      <div className="admin-card">
        <h3>{editingId ? 'Edit FAQ' : 'Add New FAQ'}</h3>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Question (Marathi)</label>
            <input type="text" value={form.questionMr}
              onChange={e => setForm({ ...form, questionMr: e.target.value })}
              placeholder="प्रश्न मराठीत..." />
          </div>
          <div className="admin-form-group">
            <label>Question (English)</label>
            <input type="text" value={form.questionEn}
              onChange={e => setForm({ ...form, questionEn: e.target.value })}
              placeholder="Question in English..." />
          </div>
        </div>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Answer (Marathi)</label>
            <textarea value={form.answerMr} onChange={e => setForm({ ...form, answerMr: e.target.value })}
              style={{ minHeight: '80px', resize: 'vertical', whiteSpace: 'pre-wrap' }}
              placeholder="उत्तर मराठीत..." />
          </div>
          <div className="admin-form-group">
            <label>Answer (English)</label>
            <textarea value={form.answerEn} onChange={e => setForm({ ...form, answerEn: e.target.value })}
              style={{ minHeight: '80px', resize: 'vertical', whiteSpace: 'pre-wrap' }}
              placeholder="Answer in English..." />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <input type="checkbox" checked={form.active}
              onChange={e => setForm({ ...form, active: e.target.checked })} />
            Active
          </label>
          <button className="admin-btn admin-btn-secondary"
            onClick={() => setPreview(form.questionMr ? form : null)}>
            Preview
          </button>
        </div>
        {preview && (
          <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
            <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>{preview.questionMr}</div>
            <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>{preview.questionEn}</div>
            <div style={{ fontSize: '14px', marginBottom: '4px', whiteSpace: 'pre-wrap' }}>{preview.answerMr}</div>
            <div style={{ fontSize: '13px', color: '#888', whiteSpace: 'pre-wrap' }}>{preview.answerEn}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : editingId ? 'Save Changes' : 'Add FAQ'}
          </button>
          {editingId && (
            <button className="admin-btn admin-btn-secondary"
              onClick={() => { setEditingId(null); setForm({ questionMr: '', questionEn: '', answerMr: '', answerEn: '', active: true }); setPreview(null); }}>
              Cancel
            </button>
          )}
        </div>
      </div>
      <div className="admin-card">
        <h3>All FAQs ({faqs.length}) — drag to reorder</h3>
        {loading ? <p>लोड होत आहे...</p> : faqs.map((faq, i) => (
          <div key={faq.id} draggable
            onDragStart={e => handleDragStart(e, faq.id)}
            onDragOver={e => handleDragOver(e, faq.id)}
            onDrop={handleDrop}
            style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px',
              marginBottom: '8px', background: faq.active ? '#f8f9fa' : '#fef2f2',
              borderRadius: '8px', border: '1px solid #e9ecef', cursor: 'grab' }}>
            <span style={{ color: '#aaa', marginTop: '2px' }}>⠿</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '14px' }}>{i + 1}. {faq.questionMr}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>{faq.questionEn}</div>
            </div>
            <span style={{ fontSize: '12px', color: faq.active ? '#28a745' : '#dc3545', fontWeight: 600, minWidth: '55px' }}>
              {faq.active ? 'Active' : 'Inactive'}
            </span>
            <button className="admin-btn admin-btn-secondary" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => { setEditingId(faq.id); setForm({ questionMr: faq.questionMr, questionEn: faq.questionEn, answerMr: faq.answerMr, answerEn: faq.answerEn, active: faq.active }); }}>
              Edit
            </button>
            <button className="admin-btn" style={{ padding: '4px 10px', fontSize: '13px',
              background: faq.active ? '#fff3cd' : '#d4edda', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              onClick={() => handleToggleActive(faq)}>
              {faq.active ? 'Deactivate' : 'Activate'}
            </button>
            <button className="admin-btn admin-btn-danger" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => handleDelete(faq.id)}>
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== AUDIO SECTION =====
function AudioSection({ showMsg }) {
  const [tab, setTab] = useState('event');

  const AUDIO_TABS = [
    { id: 'event', label: 'Event Sounds' },
    { id: 'counting', label: 'Counting Audio' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {AUDIO_TABS.map(t => (
          <button key={t.id}
            className={`admin-btn ${tab === t.id ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'event' && <EventSounds showMsg={showMsg} />}
      {tab === 'counting' && <CountingAudio showMsg={showMsg} />}
    </div>
  );
}

// ===== EVENT SOUNDS =====
const EVENT_SOUND_TYPES = [
  { id: 'exercise_complete', label: 'Exercise Complete', description: 'Plays when a student finishes one exercise (e.g. clapping sound)' },
  { id: 'rest_start', label: 'Rest Start', description: 'Plays when rest begins between sets (e.g. whistle sound)' },
];

function EventSounds({ showMsg }) {
  const [sounds, setSounds] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [audioFiles, setAudioFiles] = useState({});
  const [audioPreviews, setAudioPreviews] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'eventSounds'));
      const map = {};
      snap.docs.forEach(d => { map[d.id] = { id: d.id, ...d.data() }; });
      setSounds(map);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (eventType, file) => {
    if (!file) return;
    const err = validateFile(file);
    if (err) { showMsg(err, 'error'); return; }
    setAudioFiles(prev => ({ ...prev, [eventType]: file }));
    setAudioPreviews(prev => ({ ...prev, [eventType]: URL.createObjectURL(file) }));
  };

  const handleSave = async (eventType) => {
    const file = audioFiles[eventType];
    if (!file) { showMsg('Please select an audio file first', 'error'); return; }
    if (!window.confirm(`Upload sound for "${EVENT_SOUND_TYPES.find(e => e.id === eventType)?.label}"?`)) return;
    setSaving(eventType);
    try {
      const audRef = ref(storage, `content/event-sounds/${eventType}_${Date.now()}`);
      await uploadBytes(audRef, file);
      const audioURL = await getDownloadURL(audRef);
      await setDoc(doc(db, 'eventSounds', eventType), {
        eventType, audioURL, updatedAt: serverTimestamp()
      });
      showMsg('Sound uploaded successfully');
      setAudioFiles(prev => ({ ...prev, [eventType]: null }));
      load();
    } catch (e) {
      showMsg('Error uploading: ' + e.message, 'error');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (eventType) => {
    if (!window.confirm('Remove this sound? The app will use the default built-in sound instead.')) return;
    try {
      await deleteDoc(doc(db, 'eventSounds', eventType));
      showMsg('Sound removed');
      load();
    } catch (e) {
      showMsg('Error removing', 'error');
    }
  };

  return (
    <div>
      <div className="admin-card">
        <h3>Event Sounds</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>
          Upload custom sounds for key moments in training. If no sound is uploaded, the app uses a built-in default.
        </p>
        {loading ? <p>लोड होत आहे...</p> : EVENT_SOUND_TYPES.map(eventType => {
          const existing = sounds[eventType.id];
          const activePreview = audioPreviews[eventType.id];
          return (
            <div key={eventType.id}
              style={{ padding: '16px', marginBottom: '12px', background: '#f8f9fa',
                borderRadius: '10px', border: '1px solid #e9ecef' }}>
              <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>{eventType.label}</div>
              <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>{eventType.description}</div>
              {existing && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px',
                  background: '#e8f5e9', padding: '8px 12px', borderRadius: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#28a745', fontWeight: 600 }}>✓ Sound uploaded</span>
                  <audio controls src={existing.audioURL} style={{ height: '32px', flex: 1 }} />
                  <button className="admin-btn admin-btn-danger" style={{ padding: '4px 10px', fontSize: '12px' }}
                    onClick={() => handleDelete(eventType.id)}>
                    Remove
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <input type="file" accept="audio/*"
                  onChange={e => handleFileChange(eventType.id, e.target.files[0])}
                  style={{ fontSize: '13px' }} />
                {activePreview && <audio controls src={activePreview} style={{ height: '32px' }} />}
                {audioFiles[eventType.id] && (
                  <button className="admin-btn admin-btn-primary" style={{ padding: '6px 14px', fontSize: '13px' }}
                    onClick={() => handleSave(eventType.id)}
                    disabled={saving === eventType.id}>
                    {saving === eventType.id ? 'Uploading...' : existing ? 'Replace Sound' : 'Upload Sound'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== COUNTING AUDIO =====
function CountingAudio({ showMsg }) {
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ number: '', description: '' });
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'countingAudio'));
      setAudioFiles(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.number || 0) - (b.number || 0)));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { showMsg(err, 'error'); return; }
    setAudioFile(file);
    setAudioPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.number || !form.description) { showMsg('Number and description required', 'error'); return; }
    if (!editingId && !audioFile) { showMsg('Audio file required', 'error'); return; }
    if (!window.confirm(editingId ? 'Save changes?' : `Add audio for number ${form.number}?`)) return;
    setSaving(true);
    try {
      let audioURL = editingId ? audioFiles.find(a => a.id === editingId)?.audioURL : null;
      if (audioFile) {
        const audRef = ref(storage, `content/counting-audio/${form.number}_${Date.now()}`);
        await uploadBytes(audRef, audioFile);
        audioURL = await getDownloadURL(audRef);
      }
      const docId = editingId || `count_${form.number}_${Date.now()}`;
      await setDoc(doc(db, 'countingAudio', docId), {
        number: parseInt(form.number), description: form.description,
        audioURL, updatedAt: serverTimestamp()
      });
      showMsg(editingId ? 'Audio updated' : `Audio for ${form.number} added`);
      setForm({ number: '', description: '' });
      setAudioFile(null); setAudioPreview(null);
      setEditingId(null);
      load();
    } catch (e) {
      showMsg('Error saving: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete audio for number ${item.number}?`)) return;
    try {
      await deleteDoc(doc(db, 'countingAudio', item.id));
      showMsg('Audio deleted');
      load();
    } catch (e) {
      showMsg('Error deleting', 'error');
    }
  };

  return (
    <div>
      <div className="admin-card">
        <h3>{editingId ? 'Edit Audio File' : 'Add Counting Audio'}</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          Upload Yatin's recorded voice for each rep number (1–50).
        </p>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Number (e.g. 1, 2, 50)</label>
            <input type="number" min="1" max="50" value={form.number}
              onChange={e => setForm({ ...form, number: e.target.value })}
              placeholder="1" />
          </div>
          <div className="admin-form-group">
            <label>Description (1 line)</label>
            <input type="text" value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Yatin counting 1" />
          </div>
        </div>
        <div className="admin-form-group" style={{ marginBottom: '12px' }}>
          <label>Audio File (max 2MB)</label>
          <input type="file" accept="audio/*" onChange={handleAudioChange} />
          {audioPreview && <audio controls src={audioPreview} style={{ marginTop: '8px', width: '100%' }} />}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Uploading...' : editingId ? 'Save Changes' : 'Add Audio'}
          </button>
          {editingId && (
            <button className="admin-btn admin-btn-secondary"
              onClick={() => { setEditingId(null); setForm({ number: '', description: '' }); setAudioFile(null); setAudioPreview(null); }}>
              Cancel
            </button>
          )}
        </div>
      </div>
      <div className="admin-card">
        <h3>All Counting Audio ({audioFiles.length}/50)</h3>
        {loading ? <p>लोड होत आहे...</p> : audioFiles.map(item => (
          <div key={item.id}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
              marginBottom: '8px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
            <div style={{ minWidth: '40px', height: '40px', background: '#2563eb', borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: '16px', flexShrink: 0 }}>
              {item.number}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>{item.description}</div>
              {item.audioURL && <audio controls src={item.audioURL} style={{ height: '32px', width: '200px' }} />}
            </div>
            <button className="admin-btn admin-btn-secondary" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => { setEditingId(item.id); setForm({ number: item.number, description: item.description }); setAudioPreview(item.audioURL); }}>
              Edit
            </button>
            <button className="admin-btn admin-btn-danger" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => handleDelete(item)}>
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}